import { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';

import { authAPI } from '../data/auth';
import { authStorage } from '../data/api';

const AuthContext = createContext(null);

const AUTH_ACTIONS = Object.freeze({
  SET_USER: 'SET_USER',
  LOGOUT: 'LOGOUT',
  SET_ERROR: 'SET_ERROR',
});

const initialState = {
  user: null,
  isLoading: true,
  error: null,
};

function authReducer(state, action) {
  switch (action.type) {
    case AUTH_ACTIONS.SET_USER:
      return { ...state, user: action.payload, isLoading: false, error: null };
    case AUTH_ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, isLoading: false };
    case AUTH_ACTIONS.LOGOUT:
      return { ...initialState, isLoading: false };
    default:
      return state;
  }
}

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState, (init) => {
    const cached = authStorage.read();
    return {
      ...init,
      user: cached?.user ?? null,
      isLoading: true,
    };
  });

  const refreshMe = useCallback(async () => {
    const me = await authAPI.fetchMe();
    if (me.success) {
      dispatch({ type: AUTH_ACTIONS.SET_USER, payload: me.data });
    } else {
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
    return me;
  }, []);

  const login = useCallback(
    async (credentials) => {
      const response = await authAPI.login(credentials);
      if (!response.success) {
        dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: response.error });
        return response;
      }
      if (response.data?.user) {
        dispatch({ type: AUTH_ACTIONS.SET_USER, payload: response.data.user });
      } else {
        await refreshMe();
      }
      return response;
    },
    [refreshMe],
  );

  const register = useCallback(
    async (payload) => {
      const response = await authAPI.register(payload);
      if (!response.success) {
        dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: response.error });
        return response;
      }
      if (response.data?.user) {
        dispatch({ type: AUTH_ACTIONS.SET_USER, payload: response.data.user });
      } else {
        await refreshMe();
      }
      return response;
    },
    [refreshMe],
  );

  const logout = useCallback(async () => {
    await authAPI.logout();
    dispatch({ type: AUTH_ACTIONS.LOGOUT });
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await authAPI.bootstrap();
      const me = await authAPI.fetchMe();
      if (cancelled) return;
      if (me.success) {
        dispatch({ type: AUTH_ACTIONS.SET_USER, payload: me.data });
      } else {
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(() => {
    const profile = state.user?.profile ?? null;
    const isAuthenticated = Boolean(state.user);
    const isOnboardingComplete = Boolean(profile?.onboarding_completed);
    const onboardingStep = profile?.onboarding_step ?? 'welcome';
    return {
      ...state,
      profile,
      onboardingStep,
      login,
      register,
      logout,
      refreshMe,
      isAuthenticated: () => isAuthenticated,
      isOnboardingComplete: () => isOnboardingComplete,
    };
  }, [state, login, register, logout, refreshMe]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
